import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Alert, Badge, Button, Col, Form, Row, Spinner, Table} from "react-bootstrap";
import {toast} from "react-toastify";
import {
    createDashboardPromotionProofApi,
    fetchDashboardPromotionProofsApi,
    uploadPromoTemp,
} from "../../http/userAPI";
import ImageLightbox from "../common/ImageLightbox";

const MAX_FILE_MB = 6;
const ALLOWED_IMG = ["image/png", "image/jpeg", "image/webp"];

const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const fmtDate = (value) =>
    value
        ? new Date(value)
            .toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            })
            .replace(",", "")
        : "-";

const statusClass = (status) => {
    if (status === "approved") return "text-success";
    if (status === "rejected") return "text-danger";
    if (status === "pending") return "text-warning";
    return "text-secondary";
};

const resolveProofImageUrl = (screenshotPath) => {
    if (!screenshotPath) return "";
    const base = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    return `${base}/promo_bounty/${screenshotPath}`;
};

const fieldHeight = 184;

const DashboardPromotionProofs = ({promotion}) => {
    const promotionKey = useMemo(() => (
        String(
            promotion?.promotion_key ||
            promotion?.detail_title ||
            promotion?.card_title ||
            "dashboard-promotion"
        )
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") || "dashboard-promotion"
    ), [promotion]);

    const promotionTitle = promotion?.detail_title || promotion?.card_title || "Promotion";
    const fileInputRef = useRef(null);

    const [proofText, setProofText] = useState("");
    const [preview, setPreview] = useState(null);
    const [tempShot, setTempShot] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [fullImage, setFullImage] = useState(null);

    const loadRows = useCallback(async () => {
        if (!promotionKey) return;
        setLoading(true);
        try {
            const data = await fetchDashboardPromotionProofsApi(promotionKey);
            setRows(data?.requests || []);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to load promotion proofs", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
        } finally {
            setLoading(false);
        }
    }, [promotionKey]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const onPickFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_IMG.includes(file.type)) {
            toast.warning("Please upload PNG/JPG/WEBP image.", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
            return;
        }

        if (file.size > MAX_FILE_MB * 1024 * 1024) {
            toast.warning(`File is too large. Max ${MAX_FILE_MB} MB.`, {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
            return;
        }

        setUploading(true);
        try {
            setPreview(await fileToDataUrl(file));
            const {uploadedFile, fileHash} = await uploadPromoTemp(file);
            setTempShot({fileName: uploadedFile, hash: fileHash});
        } catch (e) {
            setPreview(null);
            setTempShot(null);
            toast.error(e?.response?.data?.message || "Upload failed. Try another image.", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
        } finally {
            setUploading(false);
        }
    };

    const canSubmit = proofText.trim().length > 0 && !!tempShot?.fileName && !uploading && !submitting;

    const onSubmit = async (event) => {
        event.preventDefault();
        if (!canSubmit) {
            toast.warning("Add proof text and upload an image.", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
            return;
        }

        setSubmitting(true);
        try {
            await createDashboardPromotionProofApi({
                promotionKey,
                promotionTitle,
                proofText: proofText.trim(),
                tempFileName: tempShot.fileName,
                fileHash: tempShot.hash,
            });
            toast.success("Promotion proof submitted for moderation.", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
            setProofText("");
            setPreview(null);
            setTempShot(null);
            await loadRows();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Submission failed", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mt-4 pt-3 border-top">
            <ImageLightbox
                src={fullImage}
                alt="Promotion proof full image"
                onClose={() => setFullImage(null)}
            />

            <div className="border rounded bg-light p-3">
                <h6 className="text-success fw-semibold mb-3">Submit Your Promotion Proof</h6>
                <Form onSubmit={onSubmit}>
                    <Row className="g-3 align-items-stretch">
                        <Col md={4} xs={12}>
                            <div className="h-100 d-flex flex-column">
                                <Form.Label className="small text-muted mb-1">Proof image</Form.Label>
                                <Form.Control
                                    ref={fileInputRef}
                                    type="file"
                                    className="d-none"
                                    accept={ALLOWED_IMG.join(",")}
                                    onChange={onPickFile}
                                />
                                <div
                                    className="position-relative border rounded bg-white p-2 d-flex align-items-center justify-content-center"
                                    style={{
                                        height: fieldHeight,
                                        minHeight: fieldHeight,
                                        overflow: "hidden",
                                    }}
                                >
                                    {preview ? (
                                        <button
                                            type="button"
                                            className="border-0 bg-transparent p-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                            style={{cursor: "zoom-in"}}
                                            onClick={() => setFullImage(preview)}
                                        >
                                            <img
                                                src={preview}
                                                alt="Promotion proof preview"
                                                className="img-fluid rounded"
                                                style={{maxHeight: fieldHeight - 18, objectFit: "contain"}}
                                            />
                                        </button>
                                    ) : (
                                        <span
                                            className="text-muted small text-center"
                                            style={{transform: "translateY(-28px)"}}
                                        >
                                            PNG/JPG/WEBP, up to {MAX_FILE_MB} MB
                                        </span>
                                    )}
                                    <Button
                                        type="button"
                                        variant={preview ? "outline-success" : "success"}
                                        size="sm"
                                        className="position-absolute top-50 start-50 translate-middle"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading || submitting}
                                        style={{
                                            minWidth: 132,
                                            boxShadow: "0 .25rem .75rem rgba(0,0,0,.12)",
                                        }}
                                    >
                                        {uploading ? "Uploading..." : preview ? "Change Image" : "Upload Image"}
                                    </Button>
                                </div>
                            </div>
                        </Col>
                        <Col md={8} xs={12}>
                            <div className="h-100 d-flex flex-column">
                                <Form.Label className="small text-muted mb-1">Proof text</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    size="sm"
                                    value={proofText}
                                    onChange={(event) => setProofText(event.target.value)}
                                    placeholder="Describe what the screenshot proves: your username, activity, source platform, or any context staff should check."
                                    style={{
                                        height: fieldHeight,
                                        minHeight: fieldHeight,
                                        resize: "vertical",
                                    }}
                                />
                                <div className="d-flex justify-content-end mt-2">
                                    <Button type="submit" variant="success" size="sm" disabled={!canSubmit}>
                                        {submitting || uploading ? <Spinner animation="border" size="sm" /> : "Submit Proof"}
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div className="mt-4" style={{overflowX: "auto", whiteSpace: "nowrap", paddingBottom: 8}}>
                <strong className="text-danger small">Your Promotion Applications:</strong>
                {loading ? (
                    <div className="text-muted small mt-1">Loading...</div>
                ) : rows.length === 0 ? (
                    <Alert variant="light" className="border small mt-2 mb-0 py-2">
                        No applications for this promotion yet.
                    </Alert>
                ) : (
                    <Table bordered hover size="sm" className="mt-2" style={{minWidth: 860, lineHeight: 1.2}}>
                        <thead className="table-light text-muted small">
                        <tr>
                            <th className="text-center">ID</th>
                            <th>Date</th>
                            <th>Photo</th>
                            <th>Proof Text</th>
                            <th className="text-center">Status</th>
                            <th>Staff Response</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((row) => {
                            const imageUrl = resolveProofImageUrl(row.screenshot_path);
                            return (
                                <tr key={row.id} className="small">
                                    <td className="text-center align-middle text-muted">{row.id}</td>
                                    <td className="align-middle text-muted">{fmtDate(row.createdAt || row.created_at)}</td>
                                    <td className="align-middle">
                                        {imageUrl ? (
                                            <button
                                                type="button"
                                                className="border-0 bg-transparent p-0"
                                                title="Open full image"
                                                onClick={() => setFullImage(imageUrl)}
                                            >
                                                <img
                                                    src={imageUrl}
                                                    alt="Promotion proof"
                                                    className="rounded border"
                                                    style={{
                                                        width: 72,
                                                        height: 48,
                                                        objectFit: "cover",
                                                        cursor: "zoom-in",
                                                    }}
                                                />
                                            </button>
                                        ) : "-"}
                                    </td>
                                    <td
                                        className="align-middle text-muted"
                                        style={{maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis"}}
                                        title={row.submission_text || ""}
                                    >
                                        {row.submission_text || "-"}
                                    </td>
                                    <td className="text-center align-middle">
                                        <Badge bg="light" text="dark" className={statusClass(row.status)}>
                                            {String(row.status || "pending").toUpperCase()}
                                        </Badge>
                                    </td>
                                    <td
                                        className="align-middle text-muted"
                                        style={{maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis"}}
                                        title={row.moderator_note || ""}
                                    >
                                        {row.moderator_note || "-"}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>
                )}
            </div>
        </div>
    );
};

export default DashboardPromotionProofs;
